import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoAdd, IoTrash, IoEye, IoCheckmark, IoAlert, IoCalendar, IoSpeedometer, IoSave } from 'react-icons/io5';
import { FaCar, FaShieldAlt, FaTools, FaEdit, FaPlus, FaTimes, FaFileAlt, FaExclamationTriangle, FaCopy, FaIdCard} from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import VehicleDetailsModal from './VehicleDetailsModal';
import moment from 'moment';
import { cars } from '../../services/api';

const VehicleInstancesPanel = ({ car, onRefresh, token }) => {  
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkAddForm, setShowBulkAddForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [serviceHistory, setServiceHistory] = useState([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [bulkAddSuccess, setBulkAddSuccess] = useState({ show: false, count: 0 });

  // Form states
  const [newVehicle, setNewVehicle] = useState({
    vin: '',
    licensePlate: '',
    condition: 'good',
    specialNotes: '',
    purchaseDate: '',
    currentMileage: 0,
    insurance: {
      provider: '',
      policyNumber: '',
      startDate: '',
      endDate: '',
      type: 'normal',
      coverageAmount: 0
    }
  });

  const [editVehicle, setEditVehicle] = useState({
    vin: '',
    licensePlate: '',
    condition: 'good',
    specialNotes: '',
    purchaseDate: '',
    currentMileage: 0
  });

  const [newService, setNewService] = useState({
    date: new Date(),
    type: 'regular maintenance',
    description: '',
    cost: '',
    odometer: '',
    serviceProvider: '',
    notes: ''
  });
  const [newInsurance, setNewInsurance] = useState({
    provider: '',
    policyNumber: '',
    startDate: '',
    endDate: '',
    type: 'normal',
    coverageAmount: 0
  });

  const [bulkLicensePlates, setBulkLicensePlates] = useState('');

  useEffect(() => {
    if (car?._id) {
      fetchVehicles();
    }
  }, [car]);

  useEffect(() => {
    if (selectedVehicle && activeTab === 'service') {
      fetchServiceHistory();
    }
  }, [selectedVehicle, activeTab]);

  const fetchVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cars.getVehicles(car._id);
      setVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceHistory = async () => {
    if (!selectedVehicle || !selectedVehicle._id) return;
    
    setServiceLoading(true);
    try {
      const response = await cars.getServiceHistory(car._id, selectedVehicle._id);
      setServiceHistory(response.data || []);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError('Failed to load service history');
    } finally {
      setServiceLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await cars.addVehicle(car._id, newVehicle);
      await fetchVehicles();
      setShowAddForm(false);
      setNewVehicle({
        vin: '',
        licensePlate: '',
        condition: 'good',
        specialNotes: '',
        purchaseDate: '',
        currentMileage: 0,
        insurance: {
          provider: '',
          policyNumber: '',
          startDate: '',
          endDate: '',
          type: 'normal',
          coverageAmount: 0
        }
      });
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error adding vehicle:', err);
      setError('Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await cars.deleteVehicle(car._id, vehicleId);
      await fetchVehicles();
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError('Failed to delete vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('type', newService.type);
      formData.append('date', newService.date.toISOString());
      formData.append('description', newService.description);
      formData.append('cost', newService.cost);
      formData.append('odometer', newService.odometer);
      formData.append('serviceProvider', newService.serviceProvider);
      formData.append('notes', newService.notes);
      
      await cars.addServiceRecord(car._id, selectedVehicle._id, formData);
      await fetchServiceHistory();
      setShowServiceForm(false);
      setNewService({
        date: new Date(),
        type: 'regular maintenance',
        description: '',
        cost: '',
        odometer: '',
        serviceProvider: '',
        notes: ''
      });
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error adding service record:', err);
      setError('Failed to add service record');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInsurance = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const insuranceData = {
        provider: newInsurance.provider,
        policyNumber: newInsurance.policyNumber,
        startDate: newInsurance.startDate,
        endDate: newInsurance.endDate,
        type: newInsurance.type,
        coverageAmount: newInsurance.type === 'mixed' ? newInsurance.coverageAmount : 0
      };

      await cars.updateInsurance(car._id, selectedVehicle._id, insuranceData);
      
      // Update the local vehicle data
      const vehicleIndex = vehicles.findIndex(v => v._id === selectedVehicle._id);
      if (vehicleIndex !== -1) {
        vehicles[vehicleIndex].insurance = insuranceData;
        setVehicles([...vehicles]);
      }
      
      setShowInsuranceForm(false);
      setSelectedVehicle(null);
      setNewInsurance({
        provider: '',
        policyNumber: '',
        startDate: '',
        endDate: '',
        type: 'normal',
        coverageAmount: 0
      });
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error updating insurance:', err);
      setError('Failed to update insurance information');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setActiveTab('details');
  };

  const handleEditVehicleClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditVehicle({
      vin: vehicle.vin || '',
      licensePlate: vehicle.licensePlate || '',
      condition: vehicle.condition || 'good',
      specialNotes: vehicle.specialNotes || '',
      purchaseDate: vehicle.purchaseDate ? moment(vehicle.purchaseDate).format('YYYY-MM-DD') : '',
      currentMileage: vehicle.currentMileage || 0
    });
    setShowEditForm(true);
  };

  const handleEditVehicle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const vehicleData = {
        ...editVehicle,
        purchaseDate: editVehicle.purchaseDate || null
      };
      
      await cars.updateVehicle(car._id, selectedVehicle._id, vehicleData);
      await fetchVehicles();
      setShowEditForm(false);
      setSelectedVehicle(null);
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      setError('Failed to update vehicle information');
    } finally {
      setLoading(false);
    }
  };

  const isInsuranceExpired = (vehicle) => {
    if (!vehicle.insurance || !vehicle.insurance.endDate) {
      return true;
    }
    return moment().isAfter(moment(vehicle.insurance.endDate));
  };

  const getInsuranceStatus = (vehicle) => {
    if (!vehicle.insurance || !vehicle.insurance.endDate) {
      return 'No insurance';
    }
    
    const endDate = moment(vehicle.insurance.endDate);
    const daysUntilExpiry = endDate.diff(moment(), 'days');
    
    if (daysUntilExpiry < 0) {
      return 'Expired';
    } else if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return 'Valid';
    }
  };

  // Helper function to get service status text
  const getServiceStatusText = (vehicle) => {
    if (!vehicle.serviceHistory || vehicle.serviceHistory.length === 0) {
      return 'No service history';
    }
    
    const lastService = vehicle.serviceHistory
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    
    const daysSinceLastService = moment().diff(moment(lastService.date), 'days');
    
    if (daysSinceLastService > 180) return 'Service overdue';
    if (daysSinceLastService > 150) return 'Service due soon';
    return 'Service up to date';
  };

  // Helper function to get service status color
  const getServiceStatusColor = (vehicle) => {
    if (!vehicle.serviceHistory || vehicle.serviceHistory.length === 0) {
      return 'text-red-600';
    }
    
    const lastService = vehicle.serviceHistory
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    
    const daysSinceLastService = moment().diff(moment(lastService.date), 'days');
    
    if (daysSinceLastService > 180) return 'text-red-600';
    if (daysSinceLastService > 150) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Find the highest TEMP VIN number
  const getNextTempVinNumber = () => {
    const tempVins = vehicles
      .filter(v => v.vin.startsWith('TEMP'))
      .map(v => {
        const match = v.vin.match(/TEMP(\d+)/);
        return match ? parseInt(match[1]) : 0;
      });
    
    const highestTempNumber = tempVins.length > 0 ? Math.max(...tempVins) : 0;
    return highestTempNumber + 1;
  };

  const handleAddBulkVehicles = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const licensePlates = bulkLicensePlates
        .split('\n')
        .map(plate => plate.trim())
        .filter(plate => plate.length > 0);
      
      let successCount = 0;
      
      for (let i = 0; i < licensePlates.length; i++) {
        const licensePlate = licensePlates[i];
        const tempVinNumber = getNextTempVinNumber() + i;
        
        const vehicleData = {
          vin: `TEMP${tempVinNumber}`,
          licensePlate: licensePlate,
          condition: 'good',
          specialNotes: '',
          purchaseDate: new Date().toISOString(),
          currentMileage: 0,
          insurance: {
            provider: 'TBD',
            policyNumber: 'TBD',
            startDate: new Date().toISOString(),
            endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString(),
            type: 'normal',
            coverageAmount: 0
          }
        };
        
        try {
          await cars.addVehicle(car._id, vehicleData);
          successCount++;
        } catch (err) {
          console.error(`Error adding vehicle ${licensePlate}:`, err);
        }
      }
      
      await fetchVehicles();
      setShowBulkAddForm(false);
      setBulkLicensePlates('');
      setBulkAddSuccess({ show: true, count: successCount });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setBulkAddSuccess({ show: false, count: 0 });
      }, 3000);
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error adding bulk vehicles:', err);
      setError('Failed to add vehicles in bulk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Enhanced Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaCar className="text-blue-600 text-lg" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Vehicle Instances for {car.make} {car.model}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage individual vehicles of this model
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkAddForm(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <FaPlus className="text-sm" />
              <span>Bulk Add</span>
            </button>
            
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <IoAdd className="text-lg" />
              <span>Add Vehicle</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Vehicle Grid */}
      <div className="p-6">
        {loading && vehicles.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <LoadingSpinner />
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <motion.div
                key={vehicle._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                {/* Vehicle Status Badge */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        vehicle.status === 'available' ? 'bg-green-500' :
                        vehicle.status === 'rented' ? 'bg-blue-500' :
                        vehicle.status === 'maintenance' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`} />
                      <span className="font-semibold text-gray-900">
                        {vehicle.licensePlate}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {/* Insurance Status Indicator */}
                      {isInsuranceExpired(vehicle) ? (
                        <div className="p-1 bg-red-100 rounded-full" title="Insurance Expired">
                          <FaExclamationTriangle className="text-red-600 text-xs" />
                        </div>
                      ) : (
                        <div className="p-1 bg-green-100 rounded-full" title="Insurance Valid">
                          <IoCheckmark className="text-green-600 text-xs" />
                        </div>
                      )}
                      
                      <button
                        onClick={() => handleVehicleSelect(vehicle)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                        title="View Details"
                      >
                        <IoEye className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">VIN:</span>
                    <span className="font-medium text-gray-900 text-xs">
                      {vehicle.vin.length > 10 ? `${vehicle.vin.substring(0, 10)}...` : vehicle.vin}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      vehicle.status === 'available' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'rented' ? 'bg-blue-100 text-blue-800' :
                      vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Mileage:</span>
                    <span className="font-medium text-gray-900">
                      {vehicle.currentMileage ? `${vehicle.currentMileage.toLocaleString()} km` : 'N/A'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Insurance:</span>
                    <span className={`font-medium ${isInsuranceExpired(vehicle) ? 'text-red-600' : 'text-green-600'}`}>
                      {getInsuranceStatus(vehicle)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Service:</span>
                    <span className={`font-medium ${getServiceStatusColor(vehicle)}`}>
                      {getServiceStatusText(vehicle)}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 border-t border-gray-100 flex space-x-2">
                  <button
                    onClick={() => handleEditVehicleClick(vehicle)}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center justify-center space-x-1"
                  >
                    <FaEdit className="text-xs" />
                    <span>Edit</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setNewInsurance({
                        provider: vehicle.insurance?.provider || '',
                        policyNumber: vehicle.insurance?.policyNumber || '',
                        startDate: vehicle.insurance?.startDate ? moment(vehicle.insurance.startDate).format('YYYY-MM-DD') : '',
                        endDate: vehicle.insurance?.endDate ? moment(vehicle.insurance.endDate).format('YYYY-MM-DD') : '',
                        type: vehicle.insurance?.type || 'normal',
                        coverageAmount: vehicle.insurance?.coverageAmount || 0
                      });
                      setShowInsuranceForm(true);
                    }}
                    className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center space-x-1"
                  >
                    <FaShieldAlt className="text-xs" />
                    <span>Insurance</span>
                  </button>
                  
                  <button
                    onClick={() => handleDeleteVehicle(vehicle._id)}
                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <IoTrash className="text-xs" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FaCar className="mx-auto text-gray-400 text-4xl mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first vehicle instance.</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <IoAdd className="text-lg" />
              <span>Add First Vehicle</span>
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mb-4 bg-red-50 text-red-700 p-4 rounded-md border border-red-200">
          <p>{error}</p>
        </div>
      )}

      {/* Enhanced Insurance Update Modal */}
      <AnimatePresence>
        {showInsuranceForm && selectedVehicle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full mx-auto overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FaShieldAlt className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Update Insurance</h3>
                      <p className="text-sm text-gray-600">{selectedVehicle.licensePlate}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowInsuranceForm(false);
                      setSelectedVehicle(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <IoClose size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpdateInsurance} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance Provider *
                  </label>
                  <input
                    type="text"
                    value={newInsurance.provider}
                    onChange={(e) => setNewInsurance({...newInsurance, provider: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    placeholder="e.g., AXA, Allianz"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Number
                  </label>
                  <input
                    type="text"
                    value={newInsurance.policyNumber}
                    onChange={(e) => setNewInsurance({...newInsurance, policyNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    placeholder="Enter policy number"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={newInsurance.startDate}
                      onChange={(e) => setNewInsurance({...newInsurance, startDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={newInsurance.endDate}
                      onChange={(e) => setNewInsurance({...newInsurance, endDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Insurance Type
                    </label>
                    <select
                      value={newInsurance.type}
                      onChange={(e) => setNewInsurance({...newInsurance, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    >
                      <option value="normal">Normal</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>

                  {newInsurance.type === 'mixed' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Coverage Amount (€)
                      </label>
                      <input
                        type="number"
                        value={newInsurance.coverageAmount}
                        onChange={(e) => setNewInsurance({...newInsurance, coverageAmount: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInsuranceForm(false);
                      setSelectedVehicle(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    {loading ? <LoadingSpinner size="sm" /> : <IoCheckmark />}
                    <span>{loading ? 'Updating...' : 'Update Insurance'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        vehicle={selectedVehicle}
        car={car}
        isOpen={!!selectedVehicle && !showEditForm && !showInsuranceForm}
        onClose={() => setSelectedVehicle(null)}
        onUpdate={() => {
          // Refresh vehicles list after update
          fetchVehicles();
        }}
      />

      {/* Add Vehicle Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-auto">
            <h3 className="text-xl font-semibold mb-4">Add New Vehicle Instance</h3>
            <form onSubmit={handleAddVehicle}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">VIN *</label>
                  <input
                    type="text"
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.vin}
                    onChange={(e) => setNewVehicle({...newVehicle, vin: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">License Plate *</label>
                  <input
                    type="text"
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.licensePlate}
                    onChange={(e) => setNewVehicle({...newVehicle, licensePlate: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Condition</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={newVehicle.condition}
                    onChange={(e) => setNewVehicle({...newVehicle, condition: e.target.value})}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Current Mileage (km)</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded"
                    value={newVehicle.currentMileage}
                    onChange={(e) => setNewVehicle({...newVehicle, currentMileage: e.target.value})}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Purchase Date</label>
                <input
                  type="date"
                  min={moment().subtract(20, 'years').format('YYYY-MM-DD')}
                  max={moment().format('YYYY-MM-DD')}
                  className="w-full p-2 border rounded"
                  value={newVehicle.purchaseDate}
                  onChange={(e) => setNewVehicle({...newVehicle, purchaseDate: e.target.value})}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Special Notes</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="2"
                  value={newVehicle.specialNotes}
                  onChange={(e) => setNewVehicle({...newVehicle, specialNotes: e.target.value})}
                ></textarea>
              </div>

              <h4 className="font-semibold mb-2 mt-4">Insurance Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Provider *</label>
                  <input
                    type="text"
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.provider}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, provider: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Policy Number</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.policyNumber}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, policyNumber: e.target.value}
                    })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.startDate}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, startDate: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.endDate}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, endDate: e.target.value}
                    })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Insurance Type *</label>
                  <select
                    required
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.type}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, type: e.target.value}
                    })}
                  >
                    <option value="normal">Normal</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Coverage Amount {newVehicle.insurance.type === 'mixed' && '*'}
                  </label>
                  <input
                    type="number"
                    required={newVehicle.insurance.type === 'mixed'}
                    disabled={newVehicle.insurance.type !== 'mixed'}
                    className="w-full p-2 border rounded"
                    value={newVehicle.insurance.coverageAmount}
                    onChange={(e) => setNewVehicle({
                      ...newVehicle, 
                      insurance: {...newVehicle.insurance, coverageAmount: parseFloat(e.target.value) || 0}
                    })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner size="sm" /> : <FaPlus className="mr-1" />} Add Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Bulk Vehicles Modal */}
      {showBulkAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold mb-4">Add Multiple Vehicles</h3>
            
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> This will create multiple vehicles with the following defaults:
              </p>
              <ul className="list-disc pl-5 mt-1 text-sm text-blue-700">
                <li>VIN: TEMPx (automatically incremented)</li>
                <li>Condition: GOOD</li>
                <li>Mileage: 0 km</li>
                <li>Purchase Date: Today</li>
                <li>Insurance: TEMP provider (valid for 1 year)</li>
              </ul>
            </div>
            
            <form onSubmit={handleAddBulkVehicles}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  License Plates (one per line, or separated by commas) *
                </label>
                <textarea
                  required
                  className="w-full p-3 border rounded-lg h-40"
                  placeholder="ABC123&#10;DEF456&#10;GHI789"
                  value={bulkLicensePlates}
                  onChange={(e) => setBulkLicensePlates(e.target.value)}
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  Enter each license plate on a new line, or separate them with commas.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBulkAddForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <><LoadingSpinner size="sm" /> Processing...</>
                  ) : (
                    <><FaCopy className="mr-1" /> Add Bulk Vehicles</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Service Record Modal */}
      {showServiceForm && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-auto">
            <h3 className="text-xl font-semibold mb-4">Add Service Record</h3>
            <form onSubmit={handleAddService}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full p-2 border rounded"
                    value={moment(newService.date).format('YYYY-MM-DD')}
                    onChange={(e) => setNewService({...newService, date: new Date(e.target.value)})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <select
                    required
                    className="w-full p-2 border rounded"
                    value={newService.type}
                    onChange={(e) => setNewService({...newService, type: e.target.value})}
                  >
                    <option value="regular maintenance">Regular Maintenance</option>
                    <option value="repair">Repair</option>
                    <option value="inspection">Inspection</option>
                    <option value="modification">Modification</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  required
                  className="w-full p-2 border rounded"
                  rows="2"
                  value={newService.description}
                  onChange={(e) => setNewService({...newService, description: e.target.value})}
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Odometer (km)</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded"
                    value={newService.odometer}
                    onChange={(e) => setNewService({...newService, odometer: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cost (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full p-2 border rounded"
                    value={newService.cost}
                    onChange={(e) => setNewService({...newService, cost: e.target.value})}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Service Provider</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newService.serviceProvider}
                  onChange={(e) => setNewService({...newService, serviceProvider: e.target.value})}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="2"
                  value={newService.notes}
                  onChange={(e) => setNewService({...newService, notes: e.target.value})}
                ></textarea>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowServiceForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner size="sm" /> : <FaPlus className="mr-1" />} Add Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Edit Vehicle Modal */}
      {showEditForm && selectedVehicle && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold flex items-center">
                    <FaEdit className="mr-2" />
                    Edit Vehicle Instance
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    {car.make} {car.model} - {selectedVehicle.licensePlate}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setSelectedVehicle(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleEditVehicle} className="p-6">
              {/* Vehicle Identification Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <FaIdCard className="text-blue-600" size={20} />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">Vehicle Identification</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      VIN Number
                    </label>
                    <input
                      type="text"
                      value={editVehicle.vin}
                      onChange={(e) => setEditVehicle({ ...editVehicle, vin: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter VIN number"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      License Plate
                    </label>
                    <input
                      type="text"
                      value={editVehicle.licensePlate}
                      onChange={(e) => setEditVehicle({ ...editVehicle, licensePlate: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter license plate"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Condition Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <IoSpeedometer className="text-green-600" size={20} />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">Vehicle Condition & Details</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Condition
                    </label>
                    <select
                      value={editVehicle.condition}
                      onChange={(e) => setEditVehicle({ ...editVehicle, condition: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Mileage (km)
                    </label>
                    <input
                      type="number"
                      value={editVehicle.currentMileage}
                      onChange={(e) => setEditVehicle({ ...editVehicle, currentMileage: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Current mileage"
                      min="0"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Purchase Date
                    </label>
                    <input
                      type="date"
                      value={editVehicle.purchaseDate}
                      onChange={(e) => setEditVehicle({ ...editVehicle, purchaseDate: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Special Notes Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                    <FaFileAlt className="text-yellow-600" size={20} />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">Special Notes</h4>
                </div>
                
                <textarea
                  value={editVehicle.specialNotes}
                  onChange={(e) => setEditVehicle({ ...editVehicle, specialNotes: e.target.value })}
                  rows="4"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter any special notes about this vehicle (damages, modifications, etc.)"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <IoSave className="mr-2" size={18} />
                      Update Vehicle
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setSelectedVehicle(null);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium flex items-center justify-center"
                >
                  <FaTimes className="mr-2" size={16} />
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default VehicleInstancesPanel;
